import { Modal, type ModalRootProps } from '@mantine/core';
import type { ReactNode } from 'react';
import { useStackedDialogCover } from './useStackedDialogCover';

interface Props extends Pick<ModalRootProps, 'size' | 'centered' | 'yOffset'> {
	opened: boolean;
	onClose: () => void;
	/** The heading, and the dialog's accessible name. */
	title: string;
	testId: string;
	children: ReactNode;
}

/**
 * A workspace dialog on the app's themed Mantine `Modal`. It composes `Modal.Root` rather than taking
 * the `Modal` shorthand so the test id lands on the dialog itself: the shorthand spreads it onto the
 * frame, which stays mounted while the dialog is closed. Opened inside a dialog that hosts stacked
 * dialogs (`useStackedDialogHost`), it covers that dialog while open, so Escape closes this one only.
 */
export const WorkspaceModal = ({
	opened,
	onClose,
	title,
	testId,
	centered = true,
	children,
	...sizing
}: Props) => {
	useStackedDialogCover(opened);

	return (
		<Modal.Root opened={opened} onClose={onClose} centered={centered} {...sizing}>
			<Modal.Overlay />
			<Modal.Content data-testid={testId}>
				<Modal.Header>
					<Modal.Title>{title}</Modal.Title>
					<Modal.CloseButton />
				</Modal.Header>
				<Modal.Body>{children}</Modal.Body>
			</Modal.Content>
		</Modal.Root>
	);
};
